import { useEffect, useMemo, useState } from 'react'
import { getUlContext } from '@/utils/ulForm'

export type OrganizationOption = {
  id: string
  name: string
  displayName?: string
}

type OrganizationPickerSdk = {
  selectOrganization: (payload: {
    organization: string
    state: string
  }) => Promise<void>
  skipOrganizationSelection: (payload?: Record<string, unknown>) => Promise<void>
  transaction?: {
    state?: string
    organizations?: OrganizationOption[]
  }
}

async function createSdk(): Promise<OrganizationPickerSdk | null> {
  try {
    const mod = await import('@auth0/auth0-acul-js/organization-picker')
    return new mod.default() as unknown as OrganizationPickerSdk
  } catch {
    return null
  }
}

/**
 * Organizações da transação, com fallback para o contexto do Universal Login
 * (`transaction.organizations`), que o SDK expõe em diferentes formatos.
 */
export function readOrganizations(
  sdk: OrganizationPickerSdk | null,
): OrganizationOption[] {
  const ctx = getUlContext() as {
    transaction?: { organizations?: OrganizationOption[] }
  } | null
  const list = sdk?.transaction?.organizations ?? ctx?.transaction?.organizations
  if (!Array.isArray(list)) return []
  return list
    .filter((org) => org && typeof org.id === 'string')
    .map((org) => ({
      id: org.id,
      name: org.name,
      displayName: org.displayName,
    }))
}

export function useOrganizationPickerManager() {
  const [sdk, setSdk] = useState<OrganizationPickerSdk | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string>()

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const instance = await createSdk()
      if (!cancelled) {
        setSdk(instance)
        setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const organizations = useMemo(
    () => readOrganizations(sdk),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sdk, loaded],
  )

  function getState(instance: OrganizationPickerSdk | null): string | undefined {
    return instance?.transaction?.state || getUlContext()?.transaction?.state
  }

  async function handleSelect(organization: string) {
    setError(undefined)
    const instance = sdk ?? (await createSdk())
    const state = getState(instance)

    if (!instance || !state) {
      console.info('[preview organization-picker]', { organization })
      window.dispatchEvent(
        new CustomEvent('alelo:acul-preview', {
          detail: {
            screen: 'organization-picker',
            action: 'selectOrganization',
            data: { organization },
          },
        }),
      )
      return
    }

    try {
      await instance.selectOrganization({ organization, state })
    } catch {
      setError('selectFailed')
      throw new Error('SELECT_FAILED')
    }
  }

  async function handleSkip() {
    setError(undefined)
    const instance = sdk ?? (await createSdk())

    if (!instance) {
      console.info('[preview organization-picker] skip')
      window.dispatchEvent(
        new CustomEvent('alelo:acul-preview', {
          detail: {
            screen: 'organization-picker',
            action: 'skipOrganizationSelection',
          },
        }),
      )
      return
    }

    try {
      await instance.skipOrganizationSelection()
    } catch {
      setError('skipFailed')
      throw new Error('SKIP_FAILED')
    }
  }

  return { handleSelect, handleSkip, organizations, error }
}
